import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import PropTypes from "prop-types";

const NeoSelect = ({
  id,
  value,
  onChange,
  options,
  placeholder = "Select…",
  disabled = false,
  className = "",
}) => {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const ref = useRef(null);

  const selected = options.find((o) => String(o.value) === String(value));
  const label = selected ? selected.label : placeholder;

  useEffect(() => {
    const onDocClick = (e) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (!open) return;
      if (e.key === "Escape") {
        setOpen(false);
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActive((i) => Math.min((i < 0 ? -1 : i) + 1, options.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActive((i) => Math.max((i < 0 ? options.length : i) - 1, 0));
      } else if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        const idx =
          active >= 0
            ? active
            : options.findIndex((o) => String(o.value) === String(value));
        if (idx >= 0) {
          onChange(options[idx].value);
          setOpen(false);
        }
      }
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, options, value, onChange, active]);

  useEffect(() => {
    if (open)
      setActive(options.findIndex((o) => String(o.value) === String(value)));
  }, [open, options, value]);

  return (
    <div
      ref={ref}
      className={classNames("neo-sel", className, {
        "is-open": open,
        "is-disabled": disabled,
      })}
    >
      <button
        id={id}
        type="button"
        className="neo-sel__control"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={`${id}-listbox`}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="neo-sel__value">{label}</span>
        <i className="neo-sel__chev" aria-hidden="true" />
      </button>

      {open && (
        <ul id={`${id}-listbox`} role="listbox" className="neo-sel__menu">
          {options.map((opt, idx) => {
            const isSel = String(opt.value) === String(value);
            return (
              <li
                key={opt.value}
                role="option"
                aria-selected={isSel}
                className={classNames("neo-sel__item", {
                  "is-selected": isSel,
                  "is-active": idx === active,
                })}
                onMouseEnter={() => setActive(idx)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
              >
                {opt.label}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

NeoSelect.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      label: PropTypes.node.isRequired,
    })
  ).isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
};

NeoSelect.defaultProps = {
  value: "",
  placeholder: "Select…",
  disabled: false,
  className: "",
};

export default NeoSelect;
